import React, { useState, useRef, useEffect, useMemo, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNavigation } from '@/contexts/NavigationContext';
import { Settings, Crown, Loader2, Clock, LogOut, ChevronDown, HelpCircle, Search, Bot, ChevronsRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useSidebar } from '@/components/ui/sidebar';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/components/AuthProvider';
import { HeaderBusinessSelector } from './HeaderBusinessSelector';
import { BusinessAccountContext } from '@/contexts/BusinessAccountContext';
import { NotificationDropdown } from '@/components/notifications/NotificationDropdown';
import { useBusinessAccounts } from '@/hooks/useBusinessAccounts';
import { dashboardApi } from '@/lib/api';
import { useCurrentSubscription } from '@/hooks/useCurrentSubscription';
import { useAiChat } from '@/contexts/GenUIContext';
import type { Subscription } from '@/lib/api/services/subscriptions';
import { isTrialActuallyExpired, isSubscriptionActive } from '@/lib/subscription-utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAvailableSearchItems } from '../search/GlobarSearch';
import { getAiAnalyticsUrl } from '@/lib/api/aiAnalyticsUrl';
import { withOrganizationId } from '@/lib/api/organization';
import { useToast } from '@/hooks/use-toast';
import { ApiError } from '@/lib/api/client';
import { showApiError } from '@/lib/errors/showApiError';
const sidebar_trigger_icon = '/assets/SidebarSimple.svg';
const notification_icon = '/assets/notification-icon.svg';
const dropdown_icon = '/assets/dropdown-close-icon.svg';
const we_next_logo = '/assets/wenext_logo_dark.png';
const small_we_next_logo = '/assets/loading-icons/we-next-icon.jpg';
const nexa_ai_icon = '/onboardingimg/wenext-icon.svg';

// Premium Button Component with Ripple Animation
const PremiumButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const [ripples, setRipples] = useState<Array<{ id: number; x: number; y: number }>>([]);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const rippleIdRef = useRef(0);

  // Create a ripple at a random position
  const createRipple = (x?: number, y?: number) => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const rippleX = x !== undefined ? x : Math.random() * rect.width;
    const rippleY = y !== undefined ? y : Math.random() * rect.height;

    const newRipple = {
      id: rippleIdRef.current++,
      x: rippleX,
      y: rippleY,
    };

    setRipples((prev) => [...prev, newRipple]);

    // Remove ripple after animation
    setTimeout(() => {
      setRipples((prev) => prev.filter((ripple) => ripple.id !== newRipple.id));
    }, 800);
  };

  // Continuous ripple animation
  useEffect(() => {
    const interval = setInterval(() => {
      createRipple();
    }, 1500); // Create a new ripple every 1.5 seconds

    return () => clearInterval(interval);
  }, []);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Create ripple at click position
    createRipple(x, y);
    onClick();
  };

  return (
    <button
      ref={buttonRef}
      onClick={handleClick}
      className="relative overflow-hidden px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 text-white font-semibold text-xs sm:text-sm shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 active:scale-95 group"
      style={{
        background: 'linear-gradient(135deg, #f59e0b 0%, #eab308 50%, #f59e0b 100%)',
      }}
    >
      {/* Shimmer effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

      {/* Ripple effects */}
      {ripples.map((ripple) => (
        <span
          key={ripple.id}
          className="absolute rounded-full bg-white/30 pointer-events-none"
          style={{
            left: `${ripple.x}px`,
            top: `${ripple.y}px`,
            width: '0px',
            height: '0px',
            transform: 'translate(-50%, -50%)',
            animation: 'ripple 800ms ease-out',
          }}
        />
      ))}

      {/* Content */}
      <span className="relative flex items-center gap-1.5 sm:gap-2 z-10">
        <Crown className="h-3 w-3 sm:h-4 sm:w-4" />
        <span className="whitespace-nowrap">Plans</span>
        {/* <Sparkles className="h-3 w-3 sm:h-4 sm:w-4 opacity-70" /> */}
      </span>

      <style>{`
        @keyframes ripple {
          to {
            width: 400px;
            height: 400px;
            opacity: 0;
          }
        }
      `}</style>
    </button>
  );
};

interface TopNavigationProps {
  onStartTour?: () => void;
  onOpenBilling?: () => void;
}

export const TopNavigation: React.FC<TopNavigationProps> = ({ onStartTour, onOpenBilling }) => {
  const { user, signOut } = useAuth();

  // Role flags derived early so the search / keyboard-shortcut effects below can
  // reference them without hitting a temporal-dead-zone error (re-derived once
  // here, then reused throughout).
  const isSuperAdmin = !!(user as any)?.is_super_admin;
  const isAgency = !!(user as any)?.is_agency;

  const [searchExpanded, setSearchExpanded] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const [isSearching, setIsSearching] = useState(false);
  // Ref mirror of isSearching for the in-flight guard — state updates are async,
  // so rapid Enter presses would all see isSearching === false without it.
  const isSearchingRef = useRef(false);
  const { toast } = useToast();
  const { toggleChat } = useAiChat();

  const handleGenUIClick = () => {
    toggleChat();
  };


  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        setSearchQuery('');
        setSearchExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Same role/plan-filtered catalog as the Ctrl+K dialog — suggesting a page
  // the user can't open just bounces them off a FeatureRoute/RoleRoute redirect.
  const availableSearchItems = useAvailableSearchItems();

  // Filter results based on query. Coming-soon entries are excluded here
  // (this inline list has no "Soon" badge, so they'd look like broken links).
  const searchResults = useMemo(() => {
    const navigable = availableSearchItems.filter((item) => !item.isComingSoon);
    if (!searchQuery) return navigable.slice(0, 6); // Return default suggestions if query is empty
    const lowerQuery = searchQuery.toLowerCase();
    return navigable.filter((item) =>
      item.title.toLowerCase().includes(lowerQuery) ||
      item.description.toLowerCase().includes(lowerQuery) ||
      item.keywords.some((k) => k.toLowerCase().includes(lowerQuery))
    ).slice(0, 6); // Limit to 6 results
  }, [searchQuery, availableSearchItems]);


  useEffect(() => {
    // Super admins and agencies don't render the client-portal search, so the
    // Ctrl/Cmd+K shortcut has nothing to focus — skip binding it for them.
    if (isSuperAdmin || isAgency) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Ctrl+K (Windows/Linux) or Cmd+K (Mac)
      const isK = e.key === 'k' || e.key === 'K';
      const isModifier = e.ctrlKey || e.metaKey;

      if (isK && isModifier) {
        e.preventDefault();
        setSearchExpanded(true);
        setShowSuggestions(true);
        // Robust focus with selection
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
          }
        }, 50);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSuperAdmin, isAgency]);
  const navigate = useNavigate();
  const { interceptedNavigate } = useNavigation();

  const runAiSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    if (isSearchingRef.current) return; // in-flight guard — Enter mashing must not stack requests/toasts
    isSearchingRef.current = true;
    setIsSearching(true);
    setShowSuggestions(false);
    try {
      const url = getAiAnalyticsUrl('/guide-chat/query');
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          withOrganizationId({ query: searchQuery, conversation_history: [] })
        ),
      });
      if (!response.ok) {
        // Preserve the backend's status + error body so showApiError can
        // resolve a specific message instead of a hardcoded generic.
        const errBody = await response.json().catch(() => ({}));
        throw ApiError.fromResult({ ...errBody, statusCode: response.status }, 'Failed to get AI suggestions');
      }
      const data = await response.json();
      if (data.message) {
        toast({ title: 'AI Assistant', description: data.message, variant: 'info' });
      }
      if (data.page_navigation?.url) {
        let path: string | null = null;
        try {
          const urlObj = new URL(data.page_navigation.url);
          path = urlObj.pathname + urlObj.search;
        } catch {
          if (data.page_navigation.url.startsWith('/')) path = data.page_navigation.url;
        }
        if (path) {
          setSearchExpanded(false);
          setSearchQuery('');
          setMobileSearchOpen(false);
          navigate(path);
        }
      }
    } catch (error) {
      // The AI assistant runs on a separate service. When it's down/unreachable,
      // fetch throws a bare network error and the catalog would blame the user's
      // internet ("Connection problem") — misleading here. Name the real culprit.
      const isNetworkClass =
        error instanceof TypeError || /failed to fetch|networkerror|load failed/i.test((error as Error)?.message || '');
      if (isNetworkClass && navigator.onLine !== false) {
        toast({
          variant: 'destructive',
          title: 'AI assistant unavailable',
          description: "We couldn't reach the AI service right now. Please try again in a few minutes.",
        });
      } else {
        showApiError(error, {
          fallback: {
            title: "AI couldn't answer that",
            description: 'The AI assistant is temporarily unavailable. Please try again in a moment.',
          },
        });
      }
    } finally {
      isSearchingRef.current = false;
      setIsSearching(false);
    }
  }, [searchQuery, navigate, toast]);
  // Safely get business account context (may be undefined for admin/agency routes)
  const businessAccountContext = useContext(BusinessAccountContext);
  const selectedAccountId = businessAccountContext?.selectedAccountId || null;
  const setSelectedAccountId = businessAccountContext?.setSelectedAccountId || (() => { });
  const { toggleSidebar, state } = useSidebar();
  const collapsed = state === 'collapsed';
  const isMobile = useIsMobile();
  const organizationId = user?.organization_id || (user as any)?.organizationId || null;

  // Determine user role and flags (isSuperAdmin / isAgency are derived at the
  // top of the component so the search effects can use them).
  const roleRaw = (user?.role || '').toString().toUpperCase();
  const isAgent = roleRaw === 'AGENT';
  const isRegularAdmin = roleRaw === 'ADMIN' && !isSuperAdmin && !isAgency;
  const isUser = roleRaw === 'USER';

  // Show Business Switcher for Regular Admins, Users, and Agents.
  // Agencies have NO business accounts (they manage referred organizations, not
  // WhatsApp Business accounts), so the switcher rendered an empty "No business
  // accounts connected" dropdown for them. They get the plain user dropdown
  // (Settings + Log out) via renderUserDropdown() instead. Super Admin is also
  // excluded.
  const showBusinessSwitcher = isRegularAdmin || isUser || isAgent;

  // Get user initials for avatar
  const getUserInitials = useMemo(() => {
    if (user?.full_name) {
      return user.full_name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return user?.email?.[0]?.toUpperCase() || 'U';
  }, [user]);

  // Shared-cache subscription query — deduplicated with AppShell/AppSidebar/etc.
  // TopNavigation owns the "poll while pending" behaviour (payment webhook
  // latency) so it passes a refetchInterval; all other consumers inherit it
  // automatically for free via the shared query entry.
  const {
    data: currentSubscription,
    isLoading: subscriptionLoading,
  } = useCurrentSubscription({
    enabled: !!organizationId && isRegularAdmin,
    staleTime: 2 * 60 * 1000,
    refetchInterval: (query) => {
      const subscription = query.state.data as any;
      if (subscription?.status === 'pending') {
        return 15 * 1000; // Poll every 15s when waiting for payment webhook
      }
      return false;
    },
  });

  const { data: headerStats, isLoading: aiCreditsLoading } = useQuery({
    queryKey: ['topnav-dashboard-stats', selectedAccountId, organizationId],
    enabled: (!!organizationId || !!selectedAccountId) && !isSuperAdmin && !isAgency,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const result = await dashboardApi.getStats(selectedAccountId, organizationId);
      return result.data;
    },
  });

  const aiCreditsAvailable = Number(headerStats?.aiCredits?.available ?? 0);
  const aiCreditsDisplayValue = aiCreditsAvailable < 0.3 ? 0 : aiCreditsAvailable;

  // Show Plans button only for regular admin (not super admin, not agency)
  const showPlansButton = isRegularAdmin;


  const handleSignOut = async () => {
    await signOut();
    // Navigate based on user type
    if (isAgency) {
      interceptedNavigate('/agency/login');
    } else if (isSuperAdmin) {
      interceptedNavigate('/admin/login');
    } else {
      interceptedNavigate('/auth');
    }
  };

  const handleProfileSettings = () => {
    if (isSuperAdmin) {
      interceptedNavigate('/admin/settings');
    } else if (isAgency) {
      interceptedNavigate('/home/agency/settings');
    } else {
      interceptedNavigate('/home/settings');
    }
  };

  const handleSettings = () => {
    handleProfileSettings();
  };

  const handleTeamSettings = () => {
    interceptedNavigate('/home/team-management');
  };

  const handleLogoClick = () => {
    if (isSuperAdmin) {
      interceptedNavigate('/admin/dashboard');
    } else if (isAgency) {
      interceptedNavigate('/home/agency/dashboard');
    } else {
      interceptedNavigate('/home');
    }
  };

  // Plan Status Component with Ripple Effect
  const PlanStatus: React.FC<{
    subscriptionLoading: boolean;
    currentSubscription: Subscription | null | undefined;
    onClick: () => void;
  }> = ({ subscriptionLoading, currentSubscription, onClick }) => {
    const [ripples, setRipples] = useState<Array<{ id: number; x: number; y: number }>>([]);
    const planStatusRef = useRef<HTMLButtonElement | HTMLDivElement>(null); // Updated ref type
    const rippleIdRef = useRef(0);

    // Derive status variables from props
    const planName = currentSubscription?.plan?.name || 'No Plan Selected';
    const status = currentSubscription?.status || 'none';
    const isPending = status === 'pending';
    const trialExpired = isTrialActuallyExpired(currentSubscription);
    const isTrialing = status === 'trialing' && !trialExpired;
    const isActive = status === 'active';
    const trialEndsAt = currentSubscription?.trial_end ? new Date(currentSubscription.trial_end) : null;
    const trialDaysRemaining =
      isTrialing && trialEndsAt
        ? Math.max(0, Math.ceil((trialEndsAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
        : null;

    // Create a ripple at a random position
    const createRipple = () => {
      if (!planStatusRef.current) return;

      const rect = planStatusRef.current.getBoundingClientRect();
      const rippleX = Math.random() * rect.width;
      const rippleY = Math.random() * rect.height;

      const newRipple = {
        id: rippleIdRef.current++,
        x: rippleX,
        y: rippleY,
      };

      setRipples((prev) => [...prev, newRipple]);

      // Remove ripple after animation
      setTimeout(() => {
        setRipples((prev) => prev.filter((ripple) => ripple.id !== newRipple.id));
      }, 800);
    };

    // Continuous ripple animation
    useEffect(() => {
      // Only create ripples if not in "Upgrade Plan" state
      if (isActive || isTrialing || isPending || subscriptionLoading) {
        const interval = setInterval(() => {
          createRipple();
        }, 1500); // Create a new ripple every 1.5 seconds

        return () => clearInterval(interval);
      }
      return () => { }; // No interval if not showing standard status
    }, [isActive, isTrialing, isPending, subscriptionLoading]); // Re-run effect if status changes

    if (subscriptionLoading) {
      return (
        <div
          aria-busy="true"
          aria-label="Loading plan"
          className="wenext-shimmer h-7 sm:h-8 w-24 sm:w-28 rounded-md sm:rounded-lg"
        />
      );
    }

    // Custom UI for "No Plan" (Upgrade CTA)
    if (!isActive && !isTrialing && !isPending && !subscriptionLoading) {
      return (
        <button
          ref={planStatusRef as any} // Cast needed because ref can be HTMLDivElement too
          onClick={onClick} // Use the onClick prop directly
          className="relative overflow-hidden px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 text-white font-semibold text-xs sm:text-sm shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 active:scale-95 group flex items-center gap-2"
          style={{
            background: 'linear-gradient(135deg, #f59e0b 0%, #eab308 50%, #f59e0b 100%)',
          }}
        >
          {/* Shimmer effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

          <Crown className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          <span className="whitespace-nowrap">Upgrade Plan</span>
        </button>
      );
    }

    // Status Classes for the standard chip
    const statusClasses = (() => {
      if (isPending) return 'bg-red-500 text-white';
      if (isTrialing) return 'bg-orange-500 text-white';
      if (isActive) return 'bg-green-500 text-white';
      return 'bg-gray-500 text-white'; // Fallback, though should be covered
    })();

    return (
      <div
        ref={planStatusRef as any}
        className={`flex items-center justify-between gap-2 sm:gap-3 px-3 py-1.5 sm:px-4 sm:py-2 rounded-md sm:rounded-lg shadow-none relative overflow-hidden ${!subscriptionLoading ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
        onClick={!subscriptionLoading ? onClick : undefined}
      >
        {/* Ripple effects */}
        {ripples.map((ripple) => (
          <span
            key={ripple.id}
            className="absolute rounded-full bg-white/20 pointer-events-none"
            style={{
              left: `${ripple.x}px`,
              top: `${ripple.y}px`,
              width: '0px',
              height: '0px',
              transform: 'translate(-50%, -50%)',
              animation: 'planRipple 800ms ease-out',
            }}
          />
        ))}

        <span className={`text-[12px] md:text-[14px] font-medium px-1.5 sm:px-2 py-0.5 rounded-full border-0 ${statusClasses} flex-shrink-0 whitespace-nowrap relative z-10`}>
          {planName}
        </span>
        {isPending && (
          <div className="flex items-center gap-1 text-[10px] sm:text-xs text-amber-200 ml-1 relative z-10">
            <Loader2 className="h-2.5 w-2.5 sm:h-3 sm:w-3 animate-spin flex-shrink-0" />
          </div>
        )}
        {isTrialing && trialDaysRemaining !== null && (
          <div className="flex items-center gap-1 text-[10px] sm:text-xs text-white/80 ml-1 relative z-10">
            <Clock className="h-2.5 w-2.5 sm:h-3 sm:w-3 flex-shrink-0" />
            <span className="whitespace-nowrap hidden sm:inline">{trialDaysRemaining} day{trialDaysRemaining === 1 ? '' : 's'}</span>
          </div>
        )}

        <style>{`
            @keyframes planRipple {
              to {
                width: 300px;
                height: 300px;
                opacity: 0;
              }
            }
          `}</style>
      </div>
    );
  };

  const renderPlanStatus = () => {
    return <PlanStatus subscriptionLoading={subscriptionLoading} currentSubscription={currentSubscription} onClick={() => {
      if (isSubscriptionActive(currentSubscription)) {
        interceptedNavigate('/home/billing');
      } else {
        if (onOpenBilling) {
          onOpenBilling();
        } else {
          interceptedNavigate('/home/billing');
        }
      }
    }} />;
  };

  const AI_CREDITS_DATA = [
    { api: 'Template generation', costPerCall: 0.438 },
    { api: 'Campaign prediction', costPerCall: 0.254 },
    { api: 'Template insights', costPerCall: 0.264 },
    { api: 'Template body refine', costPerCall: 0.134 },
    { api: 'Analytics query', costPerCall: 0.896 },
    { api: 'Guide chat', costPerCall: 0.408 },
    { api: 'Meta ads analyze', costPerCall: 0.288 },
    { api: 'Livechat', costPerCall: 0.36 },
    { api: 'WB AI Agent', costPerCall: 0.60 },
    { api: 'Automation flow', costPerCall: 0.36 },
    { api: 'Analytics', costPerCall: 0.30 },
    { api: 'Chatbot', costPerCall: 0.48 },
    { api: 'Form builder', costPerCall: 0.24 },
    { api: 'Image generator', costPerCall: 0.65 },
  ];

  const renderAiCreditsChip = () => (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-flex items-center cursor-pointer">
            {aiCreditsLoading ? (
              <div
                aria-busy="true"
                aria-label="Loading AI credits"
                className="wenext-shimmer h-7 sm:h-8 w-24 sm:w-28 rounded-md"
              />
            ) : (
              <Badge
                variant="secondary"
                className="text-xs sm:text-sm font-semibold bg-gradient-to-r from-[#132846] to-[#17345a] text-white border border-[#2a466a] px-2.5 py-1.5 shadow-sm"
              >
                <Bot className="mr-1.5 h-3.5 w-3.5 text-white" />
                AI Credits: {aiCreditsDisplayValue.toFixed(2)}
              </Badge>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="end"
          className="p-0 border border-[#2a466a] bg-[#0d1e33] text-white rounded-lg shadow-xl overflow-hidden w-72"
        >
          <div className="px-3 py-2 bg-[#132846] border-b border-[#2a466a]">
            <p className="text-xs font-semibold text-white/80">≈ Approx API calls with your credits</p>
            <p className="text-sm font-bold text-white">{aiCreditsDisplayValue.toFixed(2)} Credits available</p>
          </div>
          <div className="px-3 py-1.5 flex items-center justify-between border-b border-[#2a466a]/70">
            <span className="text-[10px] font-semibold text-white/50 uppercase tracking-wide">AI Call</span>
            <div className="flex gap-4">
              <span className="text-[10px] font-semibold text-white/50 uppercase tracking-wide">≈ Calls</span>
              <span className="text-[10px] font-semibold text-white/50 uppercase tracking-wide w-16 text-right">Per AI call</span>
            </div>
          </div>
          <div className="divide-y divide-[#2a466a]/50">
            {AI_CREDITS_DATA.map((row) => (
              <div key={row.api} className="flex items-center justify-between px-3 py-1.5">
                <span className="text-xs text-white/70 flex-1">{row.api}</span>
                <div className="flex gap-4 flex-shrink-0">
                  <span className="text-xs font-semibold text-white tabular-nums">
                    ≈ {Math.floor(aiCreditsDisplayValue / row.costPerCall).toLocaleString()}
                  </span>
                  <span className="text-xs text-white/50 tabular-nums w-16 text-right">{row.costPerCall.toFixed(3)}</span>
                </div>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  // Merged Plan + AI Credits — single segmented pill (plan zone | divider | credits zone)
  const renderPlanCreditsBar = () => {
    const status = currentSubscription?.status || 'none';
    const planName = currentSubscription?.plan?.name || 'No Plan';
    const trialExpired = isTrialActuallyExpired(currentSubscription);
    const isTrialing = status === 'trialing' && !trialExpired;
    const isActive = status === 'active';
    const isPending = status === 'pending';
    const isNoPlan = !isActive && !isTrialing && !isPending;
    const trialEndsAt = currentSubscription?.trial_end ? new Date(currentSubscription.trial_end) : null;
    const trialDaysRemaining =
      isTrialing && trialEndsAt
        ? Math.max(0, Math.ceil((trialEndsAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
        : null;
    const dotColor = isActive ? 'bg-green-500' : isTrialing ? 'bg-orange-500' : isPending ? 'bg-red-500' : 'bg-amber-500';

    const planOnClick = () => {
      if (isSubscriptionActive(currentSubscription)) {
        interceptedNavigate('/home/billing');
      } else if (onOpenBilling) {
        onOpenBilling();
      } else {
        interceptedNavigate('/home/billing');
      }
    };

    if (subscriptionLoading) {
      return (
        <div
          aria-busy="true"
          aria-label="Loading plan"
          className="wenext-shimmer h-10 w-48 rounded-lg"
        />
      );
    }

    return (
      <div className="inline-flex items-center h-10 rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        {/* Plan zone */}
        <button
          type="button"
          onClick={planOnClick}
          title={isNoPlan ? 'Upgrade your plan' : 'Manage billing'}
          className={cn(
            'flex items-center gap-1.5 h-full px-2.5 transition-colors',
            isNoPlan
              ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700'
              : 'hover:bg-slate-50'
          )}
        >
          {isNoPlan ? (
            <>
              <Crown className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="text-[13px] font-semibold whitespace-nowrap">Upgrade</span>
            </>
          ) : (
            <>
              <span className={cn('h-2 w-2 rounded-full flex-shrink-0', dotColor)} />
              <span className="text-[13px] font-semibold text-slate-700 whitespace-nowrap">{planName}</span>
              {isTrialing && trialDaysRemaining !== null && (
                <span className="flex items-center gap-0.5 text-[11px] text-orange-500 whitespace-nowrap">
                  <Clock className="h-3 w-3" />
                  {trialDaysRemaining}d
                </span>
              )}
              {isPending && <Loader2 className="h-3 w-3 animate-spin text-red-500 flex-shrink-0" />}
            </>
          )}
        </button>

        {/* Divider */}
        <div className="h-6 w-px bg-slate-200 flex-shrink-0" />

        {/* AI Credits zone */}
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1.5 h-full px-2.5 cursor-pointer hover:bg-slate-50 transition-colors">
                {aiCreditsLoading ? (
                  <div
                    aria-busy="true"
                    aria-label="Loading AI credits"
                    className="wenext-shimmer h-4 w-16 rounded"
                  />
                ) : (
                  <>
                    <Bot className="h-3.5 w-3.5 text-[var(--secondary-color)] flex-shrink-0" />
                    <span className="text-[13px] font-semibold text-slate-700 tabular-nums whitespace-nowrap">
                      {aiCreditsDisplayValue.toFixed(2)}
                    </span>
                  </>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              align="end"
              className="p-0 border border-[#2a466a] bg-[#0d1e33] text-white rounded-lg shadow-xl overflow-hidden w-72"
            >
              <div className="px-3 py-2 bg-[#132846] border-b border-[#2a466a]">
                <p className="text-xs font-semibold text-white/80">≈ Approx API calls with your credits</p>
                <p className="text-sm font-bold text-white">{aiCreditsDisplayValue.toFixed(2)} Credits available</p>
              </div>
              <div className="px-3 py-1.5 flex items-center justify-between border-b border-[#2a466a]/70">
                <span className="text-[10px] font-semibold text-white/50 uppercase tracking-wide">AI Call</span>
                <div className="flex gap-4">
                  <span className="text-[10px] font-semibold text-white/50 uppercase tracking-wide">≈ Calls</span>
                  <span className="text-[10px] font-semibold text-white/50 uppercase tracking-wide w-16 text-right">Per AI call</span>
                </div>
              </div>
              <div className="divide-y divide-[#2a466a]/50">
                {AI_CREDITS_DATA.map((row) => (
                  <div key={row.api} className="flex items-center justify-between px-3 py-1.5">
                    <span className="text-xs text-white/70 flex-1">{row.api}</span>
                    <div className="flex gap-4 flex-shrink-0">
                      <span className="text-xs font-semibold text-white tabular-nums">
                        ≈ {Math.floor(aiCreditsDisplayValue / row.costPerCall).toLocaleString()}
                      </span>
                      <span className="text-xs text-white/50 tabular-nums w-16 text-right">{row.costPerCall.toFixed(3)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  };

  // Check if user has a subscription (only when not loading)
  const hasSubscription = !subscriptionLoading && currentSubscription !== null && currentSubscription !== undefined;
  // Show PlanStatus when loading or when has subscription
  const showPlanStatus = subscriptionLoading || hasSubscription;

  // Render user dropdown (for agent, super admin, or agency)
  const renderUserDropdown = () => {
    if (!isAgent && !isSuperAdmin && !isAgency && !isUser) return null;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            data-testid="user-menu-trigger"
            variant="ghost"
            className="group flex items-center gap-2 sm:gap-2.5 h-auto p-1.5 sm:p-2 md:p-2.5 rounded-lg border-1 border-transparent transition-all duration-300 hover:border-[#434343] hover:scale-[1.02] active:scale-[0.98] focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1"
          >
            <Avatar className="h-7 w-7 sm:h-8 sm:w-8 md:h-9 md:w-9 ring-2 ring-gray-200 dark:ring-gray-700 ring-offset-1 transition-all duration-300 ">
              <AvatarImage src={user?.avatar_url} alt={user?.full_name || user?.email} />
              <AvatarFallback className="text-xs sm:text-sm font-semibold bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 transition-all duration-300">
                {getUserInitials}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col items-start min-w-0 flex-1 max-w-[80px] sm:max-w-[100px] md:max-w-[140px] lg:max-w-[160px]">
              <span className="text-xs sm:text-sm font-medium truncate w-full text-left transition-colors duration-300 group-hover:text-[#434343]">
                {user?.full_name || (isSuperAdmin ? 'Admin' : isAgency ? 'Agency User' : 'Agent')}
              </span>
              <span className="text-[10px] sm:text-xs text-muted-foreground truncate w-full text-left hidden sm:block transition-colors duration-300 group-hover:text-[#434343]">
                {isSuperAdmin ? user?.email : isAgency ? 'Agency Account' : user?.role || 'Agent'}
              </span>
            </div>
            <ChevronDown className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 text-muted-foreground flex-shrink-0 transition-all duration-300 group-hover:text-[#434343] group-data-[state=open]:rotate-180" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-56 sm:w-64 md:w-72 rounded-lg shadow-lg border bg-background/95 backdrop-blur-sm"
        >
          <DropdownMenuLabel className="px-3 py-3 sm:px-4 sm:py-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <Avatar className="h-10 w-10 sm:h-12 sm:w-12 ring-2 ring-primary/20">
                <AvatarImage src={user?.avatar_url} alt={user?.full_name || user?.email} />
                <AvatarFallback className="text-sm font-semibold bg-gradient-to-br from-primary/20 to-primary/10">
                  {getUserInitials}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col space-y-0.5 min-w-0 flex-1">
                <p className="text-sm sm:text-base font-semibold leading-none truncate">
                  {user?.full_name || (isSuperAdmin ? 'Admin' : isAgency ? 'Agency User' : 'Agent')}
                </p>
                <p className="text-xs sm:text-sm leading-none text-muted-foreground truncate">
                  {user?.email}
                </p>
                <p className="text-xs text-muted-foreground/80 mt-1 truncate">
                  {isSuperAdmin ? 'Super Admin' : isAgency ? 'Agency Account' : user?.role || 'Agent'}
                </p>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleProfileSettings}
            className="cursor-pointer focus:bg-accent transition-colors duration-150"
          >
            <Settings className="mr-2 h-4 w-4" />
            <span className="text-sm sm:text-base">Settings</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="user-menu-logout"
            onClick={handleSignOut}
            className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/20 transition-colors duration-150"
          >
            <LogOut className="mr-2 h-4 w-4" />
            <span className="text-sm sm:text-base font-medium">Log out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <>
      <header className="h-14 sm:h-16 md:h-[62px] bg-background flex items-center justify-start px-2 sm:px-4 md:px-6 py-1.5 sm:py-2 md:py-[10px] w-full gap-1.5 sm:gap-2 md:gap-3 lg:gap-4 relative border-b border-slate-200 "
        style={{
          zIndex: 40,
        }}
      >
        {/* Sidebar Toggle - Mobile only (hidden on desktop) */}
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleSidebar();
          }}
          className="h-7 w-7 sm:hidden flex-shrink-0 relative z-20"
          data-tour="sidebar-trigger"
        >
          <img src={sidebar_trigger_icon} alt="Sidebar Trigger" className="w-4 h-4" />
        </Button>

        {/* Mobile Search Trigger - Only visible below md; opens a full-width overlay.
            Hidden for super admins AND agencies (see the desktop search note). */}
        {!isSuperAdmin && !isAgency && (
          <Button
            variant="outline"
            onClick={() => {
              setMobileSearchOpen(true);
              setTimeout(() => mobileSearchInputRef.current?.focus(), 50);
            }}
            className="sm:hidden flex items-center gap-2 h-9 px-3 flex-1 min-w-0 max-w-[260px] justify-start rounded-md border border-slate-300 bg-white text-muted-foreground hover:bg-slate-50"
            aria-label="Search"
          >
            <Search className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm truncate">Ask AI...</span>
          </Button>
        )}

        {/* Expand control — only when the sidebar is collapsed (desktop). The
            icon rail is too narrow for a header button, so the expand toggle
            lives here at the left of the top bar. */}
        {collapsed && !isMobile && (
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label="Expand sidebar"
            title="Expand sidebar"
            className="hidden sm:inline-flex items-center justify-center w-10 h-10 rounded-md border border-slate-300 bg-white text-[#6a7282] hover:bg-slate-50 hover:text-[#101828] transition-colors flex-shrink-0 absolute top-1/2 -translate-y-1/2 z-20"
            style={{ left: '15px', boxShadow: '0 1px 2px rgba(16,24,40,0.04)' }}
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        )}

        {/* Desktop search — the logo now lives in the sidebar's own header, so
            the top bar starts after the sidebar and this block anchors the left
            of the (shifted) bar. Shifts right to make room for the expand button
            when the sidebar is collapsed. */}
        <div
          className="hidden sm:flex items-center h-full absolute top-0 bottom-0 z-10 transition-[left] duration-200 ease-smooth"
          style={{
            left: collapsed && !isMobile ? '55px' : '15px', // Snug beside the expand button when collapsed
            paddingLeft: '0px'
          }}
        >
          {/* Client-portal search (SEARCH_ITEMS) is hidden for super admins AND
              agencies. Every search item is a client-portal page; agencies
              navigate via their own dedicated sidebar (Dashboard / Organizations
              / Compensations / Settings), so the search only surfaced pages they
              can't reach. */}
          {!isSuperAdmin && !isAgency && (
            <div
              className="flex items-center gap-1.5 sm:gap-2 md:gap-3 flex-shrink-0 min-w-0 relative"
              ref={searchContainerRef}
            >
              {/* Inline Expandable Search */}
              <div className="hidden sm:flex items-center ml-2 relative">
                <div
                  className={cn(
                    "flex h-10 items-center rounded-md overflow-hidden border border-slate-300 bg-white transition-colors hover:bg-slate-50 cursor-text",
                    "w-[200px] md:w-[240px] lg:w-[300px] xl:w-[360px]"
                  )}
                  onClick={() => {
                    if (!searchExpanded) {
                      setSearchExpanded(true);
                    }
                    setShowSuggestions(true);
                    setTimeout(() => searchInputRef.current?.focus(), 100);
                  }}
                >
                  <div className="flex items-center justify-center h-10 w-10 flex-shrink-0 text-muted-foreground">
                    {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </div>
                  <input
                    ref={searchInputRef}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setShowSuggestions(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        runAiSearch();
                      }
                      if (e.key === 'Escape') {
                        setShowSuggestions(false);
                        setSearchQuery('');
                        searchInputRef.current?.blur();
                      }
                    }}
                    onBlur={() => {
                      // Delay hiding to allow click on results
                      setTimeout(() => {
                        // checking document.activeElement handled by handleClickOutside for now
                      }, 200);
                    }}
                    className="bg-transparent border-none outline-none text-sm placeholder:text-muted-foreground/50 text-foreground transition-all duration-300 w-full pr-2 opacity-100 min-w-0"
                    placeholder="Ask AI or Search pages..."
                  />

                  {/* Shortcut hint - Dynamic based on OS */}
                  <div className="flex items-center gap-1 pr-3 pointer-events-none select-none shrink-0">
                    <kbd className="hidden lg:inline-flex h-5 items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
                      <span className="text-xs">
                        {typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl'}
                      </span>
                      K
                    </kbd>
                  </div>

                </div>

                {/* Vertical Results */}
                {showSuggestions && (
                  <div className="absolute top-full left-0 mt-2 w-full z-50">
                    <div className="bg-background/95 backdrop-blur-xl border border-border/50 shadow-2xl rounded-xl p-2 animate-in fade-in slide-in-from-top-2 duration-200">
                      <div className="flex flex-col gap-1">
                        {searchResults.length > 0 ? (
                          searchResults.map((item) => (
                            <div
                              key={item.id}
                              onClick={() => {
                                navigate(item.path);
                                setShowSuggestions(false);
                                setSearchQuery('');
                                setSearchExpanded(false);
                              }}
                              className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/60 cursor-pointer group transition-all duration-200"
                            >
                              <div className="flex items-center justify-center p-1.5 rounded-md  text-[#fff] transition-colors shrink-0" style={{
                                background: 'var(--secondary-color)'
                              }} >
                                <item.icon className="h-4 w-4" />
                              </div>
                              <span className="text-sm font-medium text-foreground truncate">{item.title}</span>
                            </div>
                          ))
                        ) : (
                          <div className="py-6 text-center">
                            <p className="text-sm text-muted-foreground">No matches found</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Help Icon - Only for regular admin */}
              {isRegularAdmin && onStartTour && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onStartTour}
                  className="h-7 w-7 hidden sm:h-8 sm:w-8 md:h-9 md:w-9 lg:h-10 lg:w-10 flex-shrink-0"
                  title="Start guided tour"
                >
                  <HelpCircle className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground hover:text-foreground" />
                </Button>
              )}

              {/* Premium Billing Button with Ripple - Only for regular admin and if no subscription and not loading */}
              {showPlansButton && !showPlanStatus && (
                <div className="flex-shrink-0 xl:ml-[100px] ">
                  <PremiumButton onClick={() => {
                    if (onOpenBilling) {
                      onOpenBilling();
                    } else {
                      interceptedNavigate('/home/billing');
                    }
                  }} />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Left Section - Help Icon, and Plans Button */}


        {/* Right Section - Plan Status, Notifications, Business Account Selector, and User Dropdown */}
        <div className="flex items-center gap-1 sm:gap-1.5 md:gap-2 lg:gap-3 xl:gap-5 flex-shrink-0 min-w-0 py-[5px] pl-3 lg:pl-6 xl:pl-10 ml-auto">
          {/* Plan Status - Displayed before notification icon (only for regular admin and if loading or has subscription) */}
          {isRegularAdmin && showPlanStatus && (
            <div className="hidden lg:flex items-center flex-shrink-0">
              {renderPlanCreditsBar()}
            </div>
          )}

          {/* NEXA AI - opens the assistant widget; sits beside the bell icon */}
          {!isSuperAdmin && (
            <Button
              onClick={handleGenUIClick}
              title="NEXA AI"
              className="group flex-shrink-0 flex items-center gap-2 h-10 rounded-full border-0 pl-3 pr-4 text-white shadow-sm transition-all hover:shadow-md hover:brightness-105 bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600"
            >
              <img
                src={nexa_ai_icon}
                alt="WeNext"
                className="h-6 w-6 object-contain transition-transform duration-200 group-hover:scale-105"
              />
              <span className="text-sm font-bold tracking-tight">NEXA AI</span>
            </Button>
          )}

          {/* Notifications - for regular admin, agents, and users */}
          {(isRegularAdmin || isAgent || isUser) && (
            <div className="flex-shrink-0">
              <NotificationDropdown
                triggerIcon={
                  <img
                    src={notification_icon}
                    alt="Notification"
                    className="w-4 h-4 sm:w-5 sm:h-5"
                  />
                }
              />
            </div>
          )}

          {/* Business Account Selector - Show for Regular Admins, Agencies, and Users */}
          {showBusinessSwitcher && (
            <div className="flex-shrink-0 min-w-0">
              <HeaderBusinessSelector
                selectedAccountId={selectedAccountId || ''}
                onAccountChange={setSelectedAccountId}
              />
            </div>
          )}

          {/* User Dropdown - For agent, super admin, or agency (if not using business switcher) */}
          {!showBusinessSwitcher && (
            <div className="flex-shrink-0 min-w-0">
              {renderUserDropdown()}
            </div>
          )}
        </div>
      </header>

      {/* Mobile Plan Status - Shown below header on screens where the inline chips are hidden.
          Plan badge surfaces here only below lg (lg+ shows it inline). AI Credits surfaces here below xl. */}
      {isRegularAdmin && showPlanStatus && (
        <div className="lg:hidden border-b border-border bg-background px-2 py-2 flex items-center gap-2 overflow-x-auto">
          {renderPlanCreditsBar()}
        </div>
      )}

      {/* Mobile Search Overlay - opens from the mobile search trigger; below-md only */}
      {mobileSearchOpen && (
        <div
          className="sm:hidden fixed inset-0 z-[60] bg-black/40"
          onClick={() => {
            setMobileSearchOpen(false);
            setSearchQuery('');
          }}
        >
          <div
            className="bg-background border-b border-border shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 px-3 py-3">
              <div className="flex items-center flex-1 min-w-0 h-10 px-3 gap-2 rounded-md border border-slate-300 bg-white">
                {isSearching ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground flex-shrink-0" />
                ) : (
                  <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                )}
                <input
                  ref={mobileSearchInputRef}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      runAiSearch();
                    }
                    if (e.key === 'Escape') {
                      setMobileSearchOpen(false);
                      setSearchQuery('');
                    }
                  }}
                  placeholder="Ask AI or Search pages..."
                  className="bg-transparent border-none outline-none text-sm placeholder:text-muted-foreground/50 text-foreground flex-1 min-w-0"
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setMobileSearchOpen(false);
                  setSearchQuery('');
                }}
                className="h-10 px-3 text-sm font-medium"
              >
                Cancel
              </Button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-3 pb-3">
              <div className="flex flex-col gap-1">
                {searchResults.length > 0 ? (
                  searchResults.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => {
                        navigate(item.path);
                        setMobileSearchOpen(false);
                        setSearchQuery('');
                      }}
                      className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/60 active:bg-muted cursor-pointer transition-colors"
                    >
                      <div
                        className="flex items-center justify-center p-1.5 rounded-md text-white flex-shrink-0"
                        style={{ background: 'var(--secondary-color)' }}
                      >
                        <item.icon className="h-4 w-4" />
                      </div>
                      <span className="text-sm font-medium text-foreground truncate">
                        {item.title}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="py-6 text-center">
                    <p className="text-sm text-muted-foreground">No matches found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
